import React, { useEffect, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Alert,
  Typography,
  CircularProgress,
  FormControlLabel,
  Switch,
  Select,
  MenuItem,
  IconButton,
  List,
  ListItem,
  ListItemText,
  Chip,
  Box,
  Tooltip,
  Divider,
} from '@mui/material';
import {
  Bolt as BoltIcon,
  AutoAwesome as AutoAwesomeIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
} from '@mui/icons-material';
import { flowService } from '../../services/flowService';
import { useAuth } from '../../contexts/AuthContext';
import { useEnvironment } from '../../contexts/EnvironmentContext';
import { useEntitlements } from '../../contexts/EntitlementsContext';
import { track } from '../../utils/analytics';

export const SOURCE_LABELS = {
  CUSTOMER: 'Customer says',
  BUSINESS: 'You say',
};

/** Human labels for the decision log, one row per keyword/intent evaluation. */
const DECISION_LABELS = {
  SENT: 'Sent',
  SKIPPED_COOLDOWN: 'Skipped: already sent recently',
  SKIPPED_LOW_CONFIDENCE: 'Skipped: not confident enough',
  SKIPPED_NO_MATCH: "Skipped: didn't match any intent",
  SKIPPED_OWNER_ACTIVE: 'Skipped: you were already replying',
  ERROR: 'Error: nothing sent',
};

const EMPTY_FORM = {
  id: null,
  kind: 'CUSTOMER', // 'CUSTOMER' | 'BUSINESS' (keyword) | 'INTENT'
  triggerPhrase: '',
  bodyText: '',
  enabled: true,
  intentDescription: '',
  intentExamples: [],
};

/**
 * Manage the flow's auto-send triggers. Each trigger fires one of three ways:
 * - "Customer says", an inbound customer message contains the phrase (also covers
 *   ice-breaker taps and ad clicks, which arrive as ordinary inbound texts)
 * - "You say", the business's own message contains the phrase (typed from the
 *   WhatsApp Business app on a coexistence number), summoning the flow mid-chat
 * - "Customer asks about…", an inbound message is classified against the owner's own-words
 *   description by a small model; only ever routes to a flow, never a free-text reply
 * Either way the flow is sent straight into that conversation. The "Why was this sent?"
 * panel below shows the last 20 decisions the backend actually made, nothing optimistic.
 */
const FlowTriggerDialog = ({ open, onClose, flow }) => {
  const { authenticatedFetch, isDemoMode } = useAuth();
  // Intent triggers are Team (402 intentTriggers); a demo is exempt on the server, so it is here too
  const { features } = useEntitlements();
  const intentLocked = !features?.intentTriggers && !isDemoMode;
  const { selectedEnvironment } = useEnvironment();
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [triggers, setTriggers] = useState([]);
  const [form, setForm] = useState(EMPTY_FORM);
  const [exampleInput, setExampleInput] = useState('');
  const [decisions, setDecisions] = useState([]);
  const [decisionsLoading, setDecisionsLoading] = useState(false);

  useEffect(() => {
    if (!open || !flow?.id || !selectedEnvironment?.id) return;
    let cancelled = false;
    setLoading(true);
    setDecisionsLoading(true);
    setError(null);
    setForm(EMPTY_FORM);
    setExampleInput('');
    flowService.listFlowTriggers(selectedEnvironment.id, flow.id, authenticatedFetch)
      .then((list) => { if (!cancelled) setTriggers(list || []); })
      .catch((e) => { if (!cancelled) setError(e.message); })
      .finally(() => { if (!cancelled) setLoading(false); });
    flowService.listTriggerDecisions(selectedEnvironment.id, flow.id, 20, authenticatedFetch)
      .then((list) => { if (!cancelled) setDecisions(list || []); })
      .catch(() => { if (!cancelled) setDecisions([]); })
      .finally(() => { if (!cancelled) setDecisionsLoading(false); });
    return () => { cancelled = true; };
    // authenticatedFetch is recreated on every AuthContext render, depending on it would
    // re-fire this load loop while the dialog is open.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, flow?.id, selectedEnvironment?.id]);

  const handleClose = () => {
    setError(null);
    setForm(EMPTY_FORM);
    setExampleInput('');
    onClose();
  };

  const refresh = async () => {
    const [list, decisionList] = await Promise.all([
      flowService.listFlowTriggers(selectedEnvironment.id, flow.id, authenticatedFetch),
      flowService.listTriggerDecisions(selectedEnvironment.id, flow.id, 20, authenticatedFetch)
        .catch(() => []),
    ]);
    setTriggers(list || []);
    setDecisions(decisionList || []);
  };

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    const isIntent = form.kind === 'INTENT';
    const body = isIntent
      ? {
        matchType: 'INTENT',
        source: 'CUSTOMER',
        bodyText: form.bodyText.trim() || null,
        enabled: form.enabled,
        intentDescription: form.intentDescription.trim(),
        intentExamples: form.intentExamples,
      }
      : {
        triggerPhrase: form.triggerPhrase.trim(),
        matchType: 'CONTAINS',
        source: form.kind,
        bodyText: form.bodyText.trim() || null,
        enabled: form.enabled,
      };
    try {
      if (form.id) {
        await flowService.updateFlowTrigger(
          selectedEnvironment.id, flow.id, form.id, body, authenticatedFetch);
      } else {
        await flowService.createFlowTrigger(
          selectedEnvironment.id, flow.id, body, authenticatedFetch);
      }
      track('flow_trigger_saved', { flowId: flow.id, source: body.source, matchType: body.matchType, enabled: form.enabled });
      setForm(EMPTY_FORM);
      setExampleInput('');
      await refresh();
    } catch (e) {
      setError(e.message);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (trigger) => {
    setSaving(true);
    setError(null);
    try {
      await flowService.deleteFlowTrigger(
        selectedEnvironment.id, flow.id, trigger.id, authenticatedFetch);
      track('flow_trigger_removed', { flowId: flow.id, source: trigger.source });
      if (form.id === trigger.id) setForm(EMPTY_FORM);
      await refresh();
    } catch (e) {
      setError(e.message);
    } finally {
      setSaving(false);
    }
  };

  const startEdit = (trigger) => {
    const isIntent = trigger.matchType === 'INTENT';
    setForm({
      id: trigger.id,
      kind: isIntent ? 'INTENT' : trigger.source,
      triggerPhrase: trigger.triggerPhrase || '',
      bodyText: trigger.bodyText || '',
      enabled: trigger.enabled,
      intentDescription: trigger.intentDescription || '',
      intentExamples: trigger.intentExamples || [],
    });
    setExampleInput('');
  };

  const addExample = () => {
    const example = exampleInput.trim();
    if (!example || form.intentExamples.includes(example)) return;
    setForm({ ...form, intentExamples: [...form.intentExamples, example] });
    setExampleInput('');
  };

  const removeExample = (example) => {
    setForm({ ...form, intentExamples: form.intentExamples.filter((e) => e !== example) });
  };

  const isIntentKind = form.kind === 'INTENT';
  const valid = isIntentKind
    ? form.intentDescription.trim().length > 0 && form.intentDescription.trim().length <= 500
    : form.triggerPhrase.trim().length > 0 && form.triggerPhrase.trim().length <= 120;

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>Auto-send triggers</DialogTitle>
      <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: '8px !important' }}>
        <Typography variant="body2" color="text.secondary">
          “{flow?.name}” is sent into the conversation automatically, matching a{' '}
          <strong>customer’s</strong> message, <strong>your own</strong> (type the phrase from
          your WhatsApp Business app to summon the flow mid-chat), or a customer <strong>intent</strong>{' '}
          you describe in your own words.
        </Typography>

        {loading ? (
          <CircularProgress size={20} sx={{ alignSelf: 'center' }} />
        ) : (
          <>
            {triggers.length > 0 && (
              <List dense disablePadding data-testid="trigger-list">
                {triggers.map((t) => {
                  const triggerIsIntent = t.matchType === 'INTENT';
                  return (
                    <ListItem
                      key={t.id}
                      divider
                      secondaryAction={(
                        <Box>
                          <Tooltip title="Edit">
                            <IconButton size="small" onClick={() => startEdit(t)} data-testid={`trigger-edit-${t.id}`}>
                              <EditIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Remove">
                            <IconButton size="small" onClick={() => handleDelete(t)} disabled={saving} data-testid={`trigger-delete-${t.id}`}>
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      )}
                    >
                      <ListItemText
                        primary={(
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Chip
                              size="small"
                              icon={triggerIsIntent ? <AutoAwesomeIcon /> : undefined}
                              label={triggerIsIntent ? 'Customer asks about…' : (SOURCE_LABELS[t.source] || t.source)}
                              color={triggerIsIntent ? 'secondary' : (t.source === 'CUSTOMER' ? 'primary' : 'secondary')}
                              variant="outlined"
                            />
                            <Typography component="span" variant="body2" sx={{ fontWeight: 600 }}>
                              “{triggerIsIntent ? t.intentDescription : t.triggerPhrase}”
                            </Typography>
                            {!t.enabled && <Chip size="small" label="off" />}
                          </Box>
                        )}
                        secondary={t.bodyText || null}
                      />
                    </ListItem>
                  );
                })}
              </List>
            )}

            <Box sx={{ display: 'flex', gap: 1 }}>
              <Select
                size="small"
                value={form.kind}
                onChange={(e) => setForm({ ...form, kind: e.target.value })}
                sx={{ minWidth: 190 }}
                inputProps={{ 'data-testid': 'trigger-source' }}
              >
                <MenuItem value="CUSTOMER">Customer says</MenuItem>
                <MenuItem value="BUSINESS">You say</MenuItem>
                <MenuItem value="INTENT" disabled={intentLocked}>
                  Customer asks about…
                  {intentLocked && <Chip size="small" label="Team" variant="outlined" color="secondary" sx={{ ml: 1 }} />}
                </MenuItem>
              </Select>
              {!isIntentKind && (
                <TextField
                  label="Trigger phrase"
                  placeholder={form.kind === 'CUSTOMER' ? 'book' : 'let me send you the form'}
                  value={form.triggerPhrase}
                  onChange={(e) => setForm({ ...form, triggerPhrase: e.target.value })}
                  fullWidth
                  size="small"
                  inputProps={{ 'data-testid': 'trigger-phrase', maxLength: 120 }}
                />
              )}
            </Box>

            {isIntentKind && (
              <>
                <TextField
                  label="Describe the intent, in your own words"
                  placeholder="asks about booking an appointment"
                  value={form.intentDescription}
                  onChange={(e) => setForm({ ...form, intentDescription: e.target.value })}
                  fullWidth
                  size="small"
                  multiline
                  minRows={2}
                  inputProps={{ maxLength: 500 }}
                  InputProps={{ inputProps: { 'data-testid': 'trigger-intent-description', maxLength: 500 } }}
                />
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <TextField
                    label="Example phrase (optional)"
                    placeholder="do you have any slots this week?"
                    value={exampleInput}
                    onChange={(e) => setExampleInput(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        addExample();
                      }
                    }}
                    fullWidth
                    size="small"
                    inputProps={{ 'data-testid': 'trigger-intent-example-input', maxLength: 200 }}
                  />
                  <Button onClick={addExample} disabled={!exampleInput.trim()} data-testid="trigger-intent-example-add">
                    Add
                  </Button>
                </Box>
                {form.intentExamples.length > 0 && (
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }} data-testid="trigger-intent-examples">
                    {form.intentExamples.map((example) => (
                      <Chip
                        key={example}
                        label={example}
                        size="small"
                        onDelete={() => removeExample(example)}
                        data-testid={`trigger-intent-example-${example}`}
                      />
                    ))}
                  </Box>
                )}
                <Typography variant="caption" color="text.secondary">
                  Every unmatched customer message in this environment is checked against every
                  intent you configure, one send per contact per day per flow.
                </Typography>
              </>
            )}

            <TextField
              label="Message above the flow button (optional)"
              placeholder="Here's our booking form 👇"
              value={form.bodyText}
              onChange={(e) => setForm({ ...form, bodyText: e.target.value })}
              fullWidth
              size="small"
              inputProps={{ 'data-testid': 'trigger-body-text', maxLength: 640 }}
            />
            <FormControlLabel
              control={(
                <Switch
                  checked={form.enabled}
                  onChange={(e) => setForm({ ...form, enabled: e.target.checked })}
                  data-testid="trigger-enabled"
                />
              )}
              label="Trigger active"
            />
            {!isIntentKind && (
              <Typography variant="caption" color="text.secondary">
                Matching is case-insensitive, “book” matches “I’d like to BOOK an appointment”.
              </Typography>
            )}
          </>
        )}

        {flow?.status !== 'PUBLISHED' && (
          <Alert severity="info" sx={{ py: 0.5 }}>
            This flow isn’t published yet, so triggered sends go out in draft mode, only
            people with a role on your WhatsApp business can open them.
          </Alert>
        )}
        {error && <Alert severity="error" data-testid="trigger-error">{error}</Alert>}

        <Divider />
        <Box>
          <Typography variant="subtitle2" sx={{ mb: 0.5 }}>Why was this sent?</Typography>
          {decisionsLoading ? (
            <CircularProgress size={16} />
          ) : decisions.length === 0 ? (
            <Typography variant="caption" color="text.secondary">
              No activity yet. This fills in as messages are matched or classified.
            </Typography>
          ) : (
            <List dense disablePadding data-testid="decision-log">
              {decisions.map((d) => (
                <ListItem key={d.id} divider disableGutters>
                  <ListItemText
                    primary={(
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Chip
                          size="small"
                          label={d.source === 'INTENT' ? 'Intent' : 'Keyword'}
                          variant="outlined"
                          color={d.decision === 'SENT' ? 'success' : d.decision === 'ERROR' ? 'error' : 'default'}
                        />
                        <Typography variant="body2">
                          {DECISION_LABELS[d.decision] || d.decision}
                          {d.confidence != null ? ` (${Math.round(d.confidence * 100)}% confidence)` : ''}
                        </Typography>
                      </Box>
                    )}
                    secondary={[
                      d.messageExcerpt ? `“${d.messageExcerpt}”` : null,
                      d.createdAt ? new Date(d.createdAt).toLocaleString() : null,
                    ].filter(Boolean).join(' · ')}
                  />
                </ListItem>
              ))}
            </List>
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        {form.id && (
          <Button onClick={() => setForm(EMPTY_FORM)} disabled={saving}>
            Cancel edit
          </Button>
        )}
        <Button onClick={handleClose}>Close</Button>
        <Button
          variant="contained"
          onClick={handleSave}
          disabled={!valid || saving || loading}
          startIcon={saving ? <CircularProgress size={16} /> : (isIntentKind ? <AutoAwesomeIcon /> : <BoltIcon />)}
          data-testid="trigger-save"
        >
          {saving ? 'Saving…' : form.id ? 'Save changes' : 'Add trigger'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default FlowTriggerDialog;
